import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';

export interface InvoiceDetail {
  invoiceNumber: string | null;
  type: string;
  clientName: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  status: string;
  currencyCode: string | null;
  reference: string | null;
  subTotal: number;
  taxAmount: number;
  total: number;
  amountPaid: number;
  amountCredited: number;
  amountDue: number;
  payments: InvoicePaymentRow[];
  creditNotes: InvoiceCreditNoteRow[];
}

export interface InvoicePaymentRow {
  paymentDate: string | null;
  amount: number;
  reference: string | null;
}

export interface InvoiceCreditNoteRow {
  date: string | null;
  appliedAmount: number;
  creditNoteNumber: string | null;
}

@Component({
  selector: 'app-invoice-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, TabsModule],
  templateUrl: './invoice-dialog.component.html',
})
export class InvoiceDialogComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() invoice: InvoiceDetail | null = null;

  metaRows: { label: string; value: string }[] = [];
  financialRows: { label: string; value: string; negative: boolean; rawValue: number }[] = [];

  private readonly gbpFmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoice'] && this.invoice) {
      this.buildRows();
    }
  }

  formatGbp(v: number): string {
    return this.gbpFmt.format(v);
  }

  onVisibleChange(nextVisible: boolean): void {
    this.visible = nextVisible;
    this.visibleChange.emit(nextVisible);
  }

  private buildRows(): void {
    if (!this.invoice) return;
    const inv = this.invoice;
    this.metaRows = [
      { label: 'Invoice #',   value: inv.invoiceNumber ?? '' },
      { label: 'Type',        value: inv.type },
      { label: 'Contact',     value: inv.clientName ?? '' },
      { label: 'Date',        value: inv.invoiceDate ?? '' },
      { label: 'Due Date',    value: inv.dueDate ?? '' },
      { label: 'Status',      value: inv.status },
      { label: 'Currency',    value: inv.currencyCode ?? '' },
      { label: 'Reference',   value: inv.reference ?? '' },
    ];
    this.financialRows = [
      { label: 'Sub Total',       value: this.formatGbp(inv.subTotal),       negative: false, rawValue: inv.subTotal },
      { label: 'Tax',             value: this.formatGbp(inv.taxAmount),       negative: false, rawValue: inv.taxAmount },
      { label: 'Total',           value: this.formatGbp(inv.total),           negative: false, rawValue: inv.total },
      { label: 'Amount Paid',     value: this.formatGbp(inv.amountPaid),      negative: false, rawValue: inv.amountPaid },
      { label: 'Amount Credited', value: this.formatGbp(inv.amountCredited),  negative: false, rawValue: inv.amountCredited },
      { label: 'Amount Due',      value: this.formatGbp(inv.amountDue),       negative: true,  rawValue: inv.amountDue },
    ];
  }
}
